import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSearch,
  faFlask,
  faCircleExclamation,
  faCircleCheck,
  faBoxOpen,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

const HerKartSampleRequest = () => {
  const { fetchMySampleRequests, updateSampleRequest } = useHerKart();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");

  const [editingId, setEditingId] = useState(null);
  const [editStatus, setEditStatus] = useState("Requested");
  const [editReply, setEditReply] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await fetchMySampleRequests();
      if (response?.success) {
        setRequests(response.requests || []);
      }
    } catch (e) {
      console.error("Requests fetch error:", e);
      setMessage({ type: "error", text: "Could not load requests." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRequests = useMemo(() => {
    let list = [...requests];

    if (statusFilter !== "All") {
      list = list.filter((r) => r.status === statusFilter);
    }

    if (typeFilter !== "All") {
      list = list.filter((r) => r.type === typeFilter);
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      list = list.filter((r) =>
        [r.userName, r.refName, r.note].join(" ").toLowerCase().includes(keyword)
      );
    }

    return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [requests, search, statusFilter, typeFilter]);

  const openEdit = (request) => {
    setEditingId(request._id);
    setEditStatus(request.status || "Requested");
    setEditReply(request.sellerReply || "");
    setMessage(null);
  };

  const closeEdit = () => {
    setEditingId(null);
    setEditStatus("Requested");
    setEditReply("");
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      const response = await updateSampleRequest(id, { status: editStatus, sellerReply: editReply.trim() });
      if (response?.success) {
        setRequests((prev) => prev.map((r) => (r._id === id ? response.request : r)));
        setMessage({ type: "success", text: "Request updated." });
        closeEdit();
      } else {
        setMessage({ type: "error", text: "Failed to update request." });
      }
    } catch (e) {
      console.error("Update error:", e);
      setMessage({ type: "error", text: "Failed to update request." });
    } finally {
      setSaving(false);
    }
  };

  const statusBadgeClass = (status) => {
    if (status === "Fulfilled") return "bg-emerald-500/20 text-emerald-300";
    if (status === "Rejected") return "bg-red-500/20 text-red-300";
    return "bg-yellow-500/20 text-yellow-300";
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/herkart/dashboard"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-pink-300">Sample & Demo Requests</h1>
              <p className="text-xs text-slate-200">Manage requests from prospective customers</p>
            </div>
          </div>

          <div className="rounded-xl bg-pink-600/20 px-4 py-2 text-pink-300">
            <span className="font-semibold">{filteredRequests.length}</span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6">
        {message && (
          <div
            className={`mb-4 flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon icon={message.type === "success" ? faCircleCheck : faCircleExclamation} />
            {message.text}
          </div>
        )}

        <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="relative md:col-span-1">
              <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by customer, product/service, note..."
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 outline-none focus:border-pink-500"
              />
            </div>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
            >
              <option>All</option>
              <option>Sample</option>
              <option>Demo</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
            >
              <option>All</option>
              <option>Requested</option>
              <option>Fulfilled</option>
              <option>Rejected</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-200">Loading requests...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
            <FontAwesomeIcon icon={faBoxOpen} className="mx-auto mb-4 text-4xl text-slate-300" />
            <h3 className="text-xl font-semibold">No requests yet</h3>
            <p className="mt-2 text-sm text-gray-200">
              {requests.length === 0
                ? "Sample and demo requests will appear here once received."
                : "No requests match your search or filter."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((req) => (
              <div key={req._id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <h3 className="text-yellow-100 font-semibold">{req.userName}</h3>
                    <span className="flex items-center gap-1 rounded-full bg-violet-600/20 px-2 py-0.5 text-xs font-semibold text-violet-300">
                      <FontAwesomeIcon icon={faFlask} />
                      {req.type}
                    </span>
                    {req.refName && <span className="text-sm text-gray-200">for {req.refName}</span>}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(req.status)}`}>
                      {req.status}
                    </span>
                    <span className="text-xs text-slate-400">{new Date(req.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {req.note && <p className="mt-2 text-sm text-gray-200">Note: {req.note}</p>}

                {req.sellerReply && editingId !== req._id && (
                  <div className="mt-3 rounded-xl border border-pink-500/20 bg-pink-500/5 p-3">
                    <p className="text-xs font-semibold text-pink-300">Your Reply</p>
                    <p className="mt-1 text-sm text-gray-200">{req.sellerReply}</p>
                  </div>
                )}

                {editingId === req._id ? (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="mb-1 block text-sm text-gray-200">Status</label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
                      >
                        <option value="Requested">Requested</option>
                        <option value="Fulfilled">Fulfilled</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm text-gray-200">Reply (optional)</label>
                      <textarea
                        value={editReply}
                        onChange={(e) => setEditReply(e.target.value.slice(0, 250))}
                        style={{height: "100px"}}
                        maxLength={250}
                        className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 text-sm outline-none focus:border-pink-500"
                      />
                      <div className="mt-1 text-right text-xs text-slate-500">{editReply.length}/250</div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => saveEdit(req._id)}
                        disabled={saving}
                        className="rounded-xl bg-pink-600 w-fit px-2 py-1 text-xs font-semibold hover:bg-pink-500 disabled:opacity-60"
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                      <button
                        onClick={closeEdit}
                        className="rounded-xl border border-white/10 w-fit px-2 py-1 text-xs hover:bg-white/10"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => openEdit(req)}
                    className="mt-3 flex w-fit px-2 py-1 items-center gap-2 rounded-xl border border-white/10 bg-white/5 text-xs hover:bg-white/10"
                  >
                    Update Status / Reply
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HerKartSampleRequest;